import type * as React from "react";
import type { FieldValues, SubmitHandler, UseFormReturn } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";

interface ConfigFormDialogProps<TValues extends FieldValues> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  /** Required — Radix wires `aria-describedby` from it. */
  description: React.ReactNode;
  form: UseFormReturn<TValues>;
  onSubmit: SubmitHandler<TValues>;
  /** The affirmative button when idle — "Save", "Create", "Add level". */
  submitLabel: string;
  /** While true the button reads "Saving…" and is disabled. */
  pending: boolean;
  /** The `FormField`s. Laid out in a `space-y-4` column above the footer. */
  children: React.ReactNode;
}

/**
 * The add/edit dialog every Settings → Config list opens. Ten of them had their
 * own copy of this chrome — the same width, the same header, the same
 * Cancel/Save footer, the same "Saving…" — so the only thing a page should have
 * to write is its fields.
 *
 * Submitting is the form's own `onSubmit`, so the Enter key works and the
 * resolver runs; the footer's Save is a plain `type="submit"`.
 */
export function ConfigFormDialog<TValues extends FieldValues>({
  open,
  onOpenChange,
  title,
  description,
  form,
  onSubmit,
  submitLabel,
  pending,
  children,
}: ConfigFormDialogProps<TValues>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {children}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? "Saving…" : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
